'use client';

import { FAQ } from '@/components/FAQ';
import Link from 'next/link';
import React from 'react';

const SEERSavingsFAQs = () => {
	return (
		<FAQ>
			<FAQ.Title>FAQ</FAQ.Title>
			<FAQ.Block>
				<FAQ.Item>
					<FAQ.Question panelId="1">What is a SEER rating?</FAQ.Question>
					<FAQ.Answer panelId="1">
						<p>
							A SEER (Seasonal Energy Efficiency Ratio) rating measures the
							efficiency of an air conditioning system over a typical cooling
							season. The higher the SEER rating, the more efficiently the unit
							operates. SEER ratings typically range from 14 to 22, with higher
							numbers indicating better energy efficiency.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="2">
						How does the SEER Calculator work?
					</FAQ.Question>
					<FAQ.Answer panelId="2">
						<p>
							Our SEER Savings Calculator helps you understand the financial
							impact of upgrading to a cooling system with a higher SEER rating.
							This calculator compares two SEER ratings, electricity rate, home
							size, and the frequency with which you run your system. It then
							estimates your monthly, annual, and multi-year savings of a more
							efficient unit. This helps you understand if upgrading to a higher
							SEER-rated unit is worth the investment based on your specific
							usage and local energy costs.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="11">How can this help me?</FAQ.Question>
					<FAQ.Answer panelId="11">
						<p>
							The SEER Savings Calculator provides a clear picture of the
							potential cost savings when considering an HVAC upgrade. It helps
							you make informed decisions by showing whether the long-term
							savings justify the initial investment of a unit with a higher
							SEER rating. This tool can prevent misguided purchases by giving
							you a detailed cost-benefit analysis tailored to the specifics of
							your home.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="3">
						What&apos;s the difference between SEER and SEER2?
					</FAQ.Question>
					<FAQ.Answer panelId="3">
						<p>
							SEER and SEER2 both measure the efficiency of cooling systems, but
							they do it in slightly different ways.
						</p>
						<p>
							SEER has been the standard measure for many years. It calculates
							efficiency based on a fixed set of conditions, giving you an
							estimate of how much cooling you receive for the amount of energy
							used over a typical cooling season.
						</p>
						<p>
							SEER2, meanwhile, is a newer standard that provides a more
							accurate measure of efficiency. It takes into account real-world
							conditions, like the resistance of your ductwork, making it a
							better reflection of how your system will actually perform in your
							home.
						</p>
						<p>
							How SEER and SEER2 relate to each other: SEER2 is an updated
							version of SEER, designed to provide a more realistic view of your
							air conditioner&lsquo;s efficiency. Because SEER2 considers
							additional factors, its ratings are generally a bit lower than
							SEER ratings for the same unit. For example, a unit with a SEER
							rating of 16 might have a SEER2 rating of around 15.2.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="4">
						Will a higher SEER rating save me money?
					</FAQ.Question>
					<FAQ.Answer panelId="4">
						<p>
							While higher SEER ratings can lead to lower monthly energy bills,
							the savings may not justify the higher upfront costs. Our research
							shows that in many cases, the additional cost of a high-SEER unit
							may take several years to recoup through energy savings, sometimes
							longer than the unit&lsquo;s lifespan. The calculator will help
							you determine whether investing in a higher SEER rating will be
							financially beneficial for your situation.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="5">
						What are the benefits of using the SEER Savings Calculator?
					</FAQ.Question>
					<FAQ.Answer panelId="5">
						<span>Using the SEER Savings Calculator helps you:</span>
						<ul className="list-outside list-disc pl-10">
							<li>
								Understand the true financial implications of upgrading your
								HVAC system.
							</li>
							<li>
								Make informed decisions based on detailed cost-benefit analysis.
							</li>
							<li>
								Avoid unnecessary expenses by evaluating whether the energy
								savings justify the higher upfront cost of a new AC.
							</li>
							<li>
								Gain insights into your energy usage and potential savings over
								time.
							</li>
						</ul>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="6">
						Is it worth upgrading to a higher SEER HVAC system?
					</FAQ.Question>
					<FAQ.Answer panelId="6">
						<p>
							It depends on your specific situation. While higher SEER units are
							more efficient, they come with higher upfront costs. The
							calculator helps you determine if the long-term savings will
							offset these costs within a reasonable period. In many cases, a
							unit with a SEER rating of 14 or 15 provides a good balance of
							efficiency and cost.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="7">
						How can I determine my current SEER rating?
					</FAQ.Question>
					<FAQ.Answer panelId="7">
						<p>
							You can find the SEER rating of your current air conditioning unit
							on the EnergyGuide label, usually located on the unit itself. If
							it&lsquo;s not visible, check your owner&lsquo;s manual or contact
							the manufacturer with the model number.
						</p>
						<p>
							To give you a better idea of what your SEER rating might be,
							here&lsquo;s a general guideline based on the age of your unit:
						</p>
						<ul className="list-outside list-disc pl-10">
							<li>6 SEER (before the 1990s)</li>
							<li>10 SEER (1992-2005)</li>
							<li>13 SEER (2006-2015)</li>
							<li>14 SEER (2015-present, US Northern regions)</li>
							<li>15 SEER (2015-present, US Southern regions)</li>
						</ul>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="8">
						What factors affect the savings from a higher SEER rating?
					</FAQ.Question>
					<FAQ.Answer panelId="8">
						<p>
							Several factors influence the savings from a higher SEER rating,
							including:
						</p>
						<ul className="list-outside list-disc pl-10">
							<li>Local electricity rates</li>
							<li>How often you run your system</li>
							<li>
								The difference between the SEER rating of your current air
								conditioner and the rating of the new one
							</li>
							<li>The size of your home or space</li>
						</ul>
						<p>
							Our calculator takes these factors into account to provide
							accurate savings estimates.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="9">
						How accurate is the SEER Savings Calculator?
					</FAQ.Question>
					<FAQ.Answer panelId="9">
						<p>
							Our calculator is built and tested by industry experts to ensure
							accuracy. It uses standard industry formulas and guidelines to
							provide reliable estimates. However, for a customized assessment
							tailored to your specific needs, consult with one of our HVAC
							Technical Advisors to replace your HVAC.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="10">
						Can you help me replace my HVAC system?
					</FAQ.Question>
					<FAQ.Answer panelId="10">
						<p>
							Absolutely! We’re a tech company bringing a breath of fresh air to
							the HVAC world.{' '}
							<Link
								className="text-sky-700 hover:text-sky-800 hover:underline"
								href={`/replace/virtual/`}
							>
								Replace your HVAC
							</Link>{' '}
							with us and enjoy a customized solution with upfront pricing. Get
							an honest quote quickly by submitting a little information in just
							few easy steps. Our team will craft a custom solution for your
							home, ensuring you get the trusted help you deserve with fair,
							honest pricing.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
			</FAQ.Block>
		</FAQ>
	);
};

export default SEERSavingsFAQs;
